'use client';
import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { ArrowLeft, Search, CheckCircle, XCircle, AlertTriangle, Clock, ChevronDown, ChevronUp, Eye, Flag, ThumbsUp, ThumbsDown, Users, FileText, BarChart2, Send, X, Shield, MapPin, CheckSquare, Square, Layers } from 'lucide-react';



// ─── Types ────────────────────────────────────────────────────────────────────

type VerificationStatus = 'pending' | 'verified' | 'disputed' | 'approved' | 'rejected';
type DisputeReason = 'ownership_mismatch' | 'duplicate_parcel' | 'missing_docs' | 'boundary_conflict' | 'registry_gap';
type PropertyType = 'Residential' | 'Commercial' | 'Industrial' | 'Vacant Land' | 'Farmland' | 'Mixed Use';
type ActiveTab = 'queue' | 'registry' | 'analytics';

interface RegistryEntry {
  id: string;
  taxpayerId: string;
  name: string;
  nationalId: string;
  county: string;
  registeredParcels: number;
  status: 'active' | 'inactive' | 'flagged';
}

interface ParcelRecord {
  id: string;
  parcelId: string;
  ownerName: string;
  ownerTaxpayerId: string;
  county: string;
  district: string;
  propertyType: PropertyType;
  areaSqm: number;
  zoneClass: string;
  status: VerificationStatus;
  disputeReason?: DisputeReason;
  disputeNote?: string;
  registryMatch: 'matched' | 'partial' | 'unmatched';
  registryConfidence: number;
  submittedDate: string;
  reviewedBy?: string;
  reviewedDate?: string;
  reviewNote?: string;
  sourceStage: 'S2';
  selected: boolean;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const TAXPAYER_REGISTRY: RegistryEntry[] = [
  { id: 'TR-001', taxpayerId: 'LRA-TP-10041', name: 'Albert Sirleaf', nationalId: 'LBR-2001-441', county: 'Montserrado', registeredParcels: 2, status: 'active' },
  { id: 'TR-002', taxpayerId: 'LRA-TP-10042', name: 'Grace Tamba', nationalId: 'LBR-1998-882', county: 'Montserrado', registeredParcels: 1, status: 'active' },
  { id: 'TR-003', taxpayerId: 'LRA-TP-10043', name: 'David Nimely', nationalId: 'LBR-2003-119', county: 'Montserrado', registeredParcels: 3, status: 'flagged' },
  { id: 'TR-004', taxpayerId: 'LRA-TP-10044', name: 'Hawa Mulbah', nationalId: 'LBR-1995-334', county: 'Margibi', registeredParcels: 1, status: 'active' },
  { id: 'TR-005', taxpayerId: 'LRA-TP-10045', name: 'Thomas Konneh', nationalId: 'LBR-2000-776', county: 'Bong', registeredParcels: 0, status: 'inactive' },
  { id: 'TR-006', taxpayerId: 'LRA-TP-10046', name: 'James Kollie', nationalId: 'LBR-1997-553', county: 'Montserrado', registeredParcels: 1, status: 'active' },
  { id: 'TR-007', taxpayerId: 'LRA-TP-10047', name: 'Fatu Kamara', nationalId: 'LBR-2002-228', county: 'Montserrado', registeredParcels: 2, status: 'active' },
  { id: 'TR-008', taxpayerId: 'LRA-TP-10048', name: 'Emmanuel Doe', nationalId: 'LBR-1999-661', county: 'Margibi', registeredParcels: 1, status: 'active' },
];

const INITIAL_PARCELS: ParcelRecord[] = [
  {
    id: 'pv1', parcelId: 'LRA-MNT-2024-0001', ownerName: 'Albert Sirleaf', ownerTaxpayerId: 'LRA-TP-10041',
    county: 'Montserrado', district: 'Paynesville', propertyType: 'Residential', areaSqm: 380,
    zoneClass: 'Zone B', status: 'approved', registryMatch: 'matched', registryConfidence: 98,
    submittedDate: '2024-11-18', reviewedBy: 'Admin: K. Freeman', reviewedDate: '2024-11-19',
    reviewNote: 'Full registry match confirmed.', sourceStage: 'S2', selected: false,
  },
  {
    id: 'pv2', parcelId: 'LRA-MNT-2024-0002', ownerName: 'Grace Tamba', ownerTaxpayerId: 'LRA-TP-10042',
    county: 'Montserrado', district: 'Congo Town', propertyType: 'Commercial', areaSqm: 760,
    zoneClass: 'Zone A', status: 'verified', registryMatch: 'matched', registryConfidence: 94,
    submittedDate: '2024-11-19', sourceStage: 'S2', selected: false,
  },
  {
    id: 'pv3', parcelId: 'LRA-MNT-2024-0003', ownerName: 'David Nimely', ownerTaxpayerId: 'LRA-TP-10043',
    county: 'Montserrado', district: 'Sinkor', propertyType: 'Mixed Use', areaSqm: 1100,
    zoneClass: 'Zone A', status: 'disputed', registryMatch: 'partial', registryConfidence: 61,
    disputeReason: 'ownership_mismatch', disputeNote: 'Registry shows co-ownership with M. Nimely not declared in S2.',
    submittedDate: '2024-11-19', reviewedBy: 'Admin: K. Freeman', reviewedDate: '2024-11-20',
    sourceStage: 'S2', selected: false,
  },
  {
    id: 'pv4', parcelId: 'LRA-MRG-2024-0001', ownerName: 'Hawa Mulbah', ownerTaxpayerId: 'LRA-TP-10044',
    county: 'Margibi', district: 'Kakata', propertyType: 'Farmland', areaSqm: 6400,
    zoneClass: 'Zone E', status: 'pending', registryMatch: 'matched', registryConfidence: 89,
    submittedDate: '2024-11-20', sourceStage: 'S2', selected: false,
  },
  {
    id: 'pv5', parcelId: 'LRA-BNG-2024-0001', ownerName: 'Thomas Konneh', ownerTaxpayerId: 'LRA-TP-10045',
    county: 'Bong', district: 'Gbarnga', propertyType: 'Industrial', areaSqm: 2200,
    zoneClass: 'Zone C', status: 'pending', registryMatch: 'unmatched', registryConfidence: 22,
    submittedDate: '2024-11-20', sourceStage: 'S2', selected: false,
  },
  {
    id: 'pv6', parcelId: 'LRA-MNT-2024-0004', ownerName: 'James Kollie', ownerTaxpayerId: 'LRA-TP-10046',
    county: 'Montserrado', district: 'Congo Town', propertyType: 'Residential', areaSqm: 420,
    zoneClass: 'Zone B', status: 'rejected', registryMatch: 'unmatched', registryConfidence: 18,
    disputeReason: 'missing_docs', disputeNote: 'No deed of conveyance found in registry. Ownership unverifiable.',
    submittedDate: '2024-11-17', reviewedBy: 'Admin: K. Freeman', reviewedDate: '2024-11-18',
    reviewNote: 'Rejected — owner must resubmit with valid title documents.', sourceStage: 'S2', selected: false,
  },
  {
    id: 'pv7', parcelId: 'LRA-MNT-2024-0005', ownerName: 'Fatu Kamara', ownerTaxpayerId: 'LRA-TP-10047',
    county: 'Montserrado', district: 'Sinkor', propertyType: 'Commercial', areaSqm: 870,
    zoneClass: 'Zone A', status: 'pending', registryMatch: 'matched', registryConfidence: 91,
    submittedDate: '2024-11-21', sourceStage: 'S2', selected: false,
  },
  {
    id: 'pv8', parcelId: 'LRA-MRG-2024-0002', ownerName: 'Emmanuel Doe', ownerTaxpayerId: 'LRA-TP-10048',
    county: 'Margibi', district: 'Kakata', propertyType: 'Farmland', areaSqm: 5200,
    zoneClass: 'Zone E', status: 'disputed', registryMatch: 'partial', registryConfidence: 55,
    disputeReason: 'boundary_conflict', disputeNote: 'Boundary overlaps with adjacent parcel LRA-MRG-2023-0018.',
    submittedDate: '2024-11-21', reviewedBy: 'Admin: K. Freeman', reviewedDate: '2024-11-22',
    sourceStage: 'S2', selected: false,
  },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<VerificationStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  pending:  { label: 'Pending',  color: '#94a3b8', bg: 'bg-slate-700/60',     icon: Clock },
  verified: { label: 'Verified', color: '#60a5fa', bg: 'bg-blue-900/40',      icon: Shield },
  disputed: { label: 'Disputed', color: '#f59e0b', bg: 'bg-amber-900/40',     icon: AlertTriangle },
  approved: { label: 'Approved', color: '#34d399', bg: 'bg-emerald-900/40',   icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#fb7185', bg: 'bg-rose-900/40',      icon: XCircle },
};

const MATCH_CONFIG: Record<'matched' | 'partial' | 'unmatched', { label: string; color: string }> = {
  matched:   { label: 'Registry Match',   color: '#34d399' },
  partial:   { label: 'Partial Match',    color: '#f59e0b' },
  unmatched: { label: 'No Match',         color: '#fb7185' },
};

const DISPUTE_LABELS: Record<DisputeReason, string> = {
  ownership_mismatch: 'Ownership Mismatch',
  duplicate_parcel:   'Duplicate Parcel',
  missing_docs:       'Missing Documents',
  boundary_conflict:  'Boundary Conflict',
  registry_gap:       'Registry Gap',
};

// ─── Review Modal ─────────────────────────────────────────────────────────────

interface ReviewModalProps {
  parcel: ParcelRecord;
  onClose: () => void;
  onApprove: (id: string, note: string) => void;
  onReject: (id: string, note: string) => void;
  onFlag: (id: string, reason: DisputeReason, note: string) => void;
}

function ReviewModal({ parcel, onClose, onApprove, onReject, onFlag }: ReviewModalProps) {
  const [action, setAction] = useState<'approve' | 'reject' | 'flag' | null>(null);
  const [note, setNote] = useState('');
  const [disputeReason, setDisputeReason] = useState<DisputeReason>('ownership_mismatch');

  const registryEntry = TAXPAYER_REGISTRY.find(r => r.taxpayerId === parcel.ownerTaxpayerId);
  const StatusIcon = STATUS_CONFIG[parcel.status].icon;
  const matchCfg = MATCH_CONFIG[parcel.registryMatch];

  const handleSubmit = () => {
    if (!action) return;
    if (action === 'approve') onApprove(parcel.id, note);
    else if (action === 'reject') onReject(parcel.id, note);
    else if (action === 'flag') onFlag(parcel.id, disputeReason, note);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700/60 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/60">
          <div>
            <p className="text-xs font-mono text-slate-500">{parcel.parcelId}</p>
            <h2 className="text-lg font-semibold text-white">Ownership Verification Review</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Parcel Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800/60 rounded-lg p-3 space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Owner (S2 Claim)</p>
              <p className="text-sm font-semibold text-white">{parcel.ownerName}</p>
              <p className="text-xs font-mono text-slate-400">{parcel.ownerTaxpayerId}</p>
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Registry Record</p>
              {registryEntry ? (
                <>
                  <p className="text-sm font-semibold text-white">{registryEntry.name}</p>
                  <p className="text-xs font-mono text-slate-400">{registryEntry.nationalId}</p>
                </>
              ) : (
                <p className="text-sm text-rose-400">Not found in registry</p>
              )}
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Property</p>
              <p className="text-sm text-white">{parcel.propertyType} · {parcel.zoneClass}</p>
              <p className="text-xs text-slate-400">{parcel.county}, {parcel.district}</p>
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Registry Match</p>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: matchCfg.color }} />
                <p className="text-sm font-semibold" style={{ color: matchCfg.color }}>{matchCfg.label}</p>
              </div>
              <p className="text-xs text-slate-400">Confidence: {parcel.registryConfidence}%</p>
            </div>
          </div>

          {/* Confidence Bar */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-400">Registry Confidence Score</span>
              <span className="text-xs font-semibold" style={{ color: parcel.registryConfidence >= 80 ? '#34d399' : parcel.registryConfidence >= 50 ? '#f59e0b' : '#fb7185' }}>
                {parcel.registryConfidence}%
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${parcel.registryConfidence}%`,
                  background: parcel.registryConfidence >= 80 ? '#34d399' : parcel.registryConfidence >= 50 ? '#f59e0b' : '#fb7185'
                }}
              />
            </div>
          </div>

          {/* Existing dispute note */}
          {parcel.disputeNote && (
            <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-3 flex gap-2">
              <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-300 mb-0.5">
                  {parcel.disputeReason ? DISPUTE_LABELS[parcel.disputeReason] : 'Dispute'}
                </p>
                <p className="text-xs text-amber-200/80">{parcel.disputeNote}</p>
              </div>
            </div>
          )}

          {/* Action Selection */}
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Admin Decision</p>
            <div className="grid grid-cols-3 gap-2">
              {(['approve', 'reject', 'flag'] as const).map(a => (
                <button
                  key={a}
                  onClick={() => setAction(a)}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    action === a
                      ? a === 'approve' ? 'bg-emerald-600 border-emerald-500 text-white'
                        : a === 'reject'? 'bg-rose-600 border-rose-500 text-white' :'bg-amber-600 border-amber-500 text-white' :'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-500'
                  }`}
                >
                  {a === 'approve' && <ThumbsUp size={14} />}
                  {a === 'reject' && <ThumbsDown size={14} />}
                  {a === 'flag' && <Flag size={14} />}
                  {a === 'approve' ? 'Approve' : a === 'reject' ? 'Reject' : 'Flag Dispute'}
                </button>
              ))}
            </div>
          </div>

          {/* Flag reason */}
          {action === 'flag' && (
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wide block mb-1.5">Dispute Reason</label>
              <select
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value as DisputeReason)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
              >
                {(Object.entries(DISPUTE_LABELS) as [DisputeReason, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          )}

          {/* Note */}
          {action && (
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wide block mb-1.5">
                {action === 'approve' ? 'Approval Note (optional)' : action === 'reject' ? 'Rejection Reason *' : 'Dispute Detail *'}
              </label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={3}
                placeholder={
                  action === 'approve' ? 'e.g. All documents verified, registry confirmed.'
                  : action === 'reject' ? 'e.g. Title deed missing, ownership unverifiable.'
                  : 'e.g. Co-owner not declared, boundary overlaps adjacent parcel.'
                }
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700/60">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!action || (action !== 'approve' && !note.trim())}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
              action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
              : action === 'reject'? 'bg-rose-600 hover:bg-rose-500 text-white' :'bg-amber-600 hover:bg-amber-500 text-white'
            }`}
          >
            {action === 'approve' ? 'Approve Parcel' : action === 'reject' ? 'Reject Parcel' : 'Flag Dispute'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Forward to S4 Modal ──────────────────────────────────────────────────────

interface ForwardModalProps {
  parcels: ParcelRecord[];
  onClose: () => void;
  onConfirm: () => void;
}

function ForwardModal({ parcels, onClose, onConfirm }: ForwardModalProps) {
  const eligible = parcels.filter(p => p.status === 'approved');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700/60 rounded-xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/60">
          <h2 className="text-lg font-semibold text-white">Forward to Stage 4 Valuation</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-3 flex gap-2">
            <CheckCircle size={15} className="text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-200">
              <span className="font-semibold">{eligible.length} approved parcel{eligible.length !== 1 ? 's' : ''}</span> will be dispatched to Stage 4 for property valuation.
            </p>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {eligible.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2">
                <div>
                  <p className="text-xs font-mono text-slate-400">{p.parcelId}</p>
                  <p className="text-sm text-white">{p.ownerName}</p>
                </div>
                <span className="text-xs text-slate-400">{p.propertyType}</span>
              </div>
            ))}
          </div>
          {eligible.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No approved parcels available to forward.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700/60">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            disabled={eligible.length === 0}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-all"
          >
            <Send size={14} />
            Forward {eligible.length} Parcel{eligible.length !== 1 ? 's' : ''} → S4
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function OwnershipVerificationClient() {
  const [parcels, setParcels] = useState<ParcelRecord[]>(INITIAL_PARCELS);
  const [activeTab, setActiveTab] = useState<ActiveTab>('queue');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<VerificationStatus | 'all'>('all');
  const [matchFilter, setMatchFilter] = useState<'all' | 'matched' | 'partial' | 'unmatched'>('all');
  const [reviewParcel, setReviewParcel] = useState<ParcelRecord | null>(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardedCount, setForwardedCount] = useState(0);
  const [sortField, setSortField] = useState<'parcelId' | 'ownerName' | 'registryConfidence' | 'submittedDate'>('submittedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // KPIs
  const kpis = useMemo(() => ({
    total: parcels.length,
    pending: parcels.filter(p => p.status === 'pending').length,
    verified: parcels.filter(p => p.status === 'verified').length,
    disputed: parcels.filter(p => p.status === 'disputed').length,
    approved: parcels.filter(p => p.status === 'approved').length,
    rejected: parcels.filter(p => p.status === 'rejected').length,
    matchRate: Math.round(parcels.filter(p => p.registryMatch === 'matched').length / parcels.length * 100),
  }), [parcels]);

  // Filtered + sorted parcels
  const filteredParcels = useMemo(() => {
    let list = parcels.filter(p => {
      const q = search.toLowerCase();
      const matchesSearch = !q || p.parcelId.toLowerCase().includes(q) || p.ownerName.toLowerCase().includes(q) || p.county.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesMatch = matchFilter === 'all' || p.registryMatch === matchFilter;
      return matchesSearch && matchesStatus && matchesMatch;
    });
    list = [...list].sort((a, b) => {
      let av: string | number = a[sortField] as string | number;
      let bv: string | number = b[sortField] as string | number;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [parcels, search, statusFilter, matchFilter, sortField, sortDir]);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => (
    sortField === field
      ? sortDir === 'asc' ? <ChevronUp size={12} className="text-blue-400" /> : <ChevronDown size={12} className="text-blue-400" />
      : <ChevronDown size={12} className="text-slate-600" />
  );

  // Actions
  const handleApprove = (id: string, note: string) => {
    setParcels(ps => ps.map(p => p.id === id ? { ...p, status: 'approved', reviewNote: note, reviewedBy: 'Admin: K. Freeman', reviewedDate: new Date().toISOString().slice(0, 10) } : p));
  };
  const handleReject = (id: string, note: string) => {
    setParcels(ps => ps.map(p => p.id === id ? { ...p, status: 'rejected', reviewNote: note, reviewedBy: 'Admin: K. Freeman', reviewedDate: new Date().toISOString().slice(0, 10) } : p));
  };
  const handleFlag = (id: string, reason: DisputeReason, note: string) => {
    setParcels(ps => ps.map(p => p.id === id ? { ...p, status: 'disputed', disputeReason: reason, disputeNote: note, reviewedBy: 'Admin: K. Freeman', reviewedDate: new Date().toISOString().slice(0, 10) } : p));
  };
  const handleForwardConfirm = () => {
    const count = parcels.filter(p => p.status === 'approved').length;
    setParcels(ps => ps.map(p => p.status === 'approved' ? { ...p, status: 'approved' } : p));
    setForwardedCount(c => c + count);
  };

  // Bulk select
  const allSelected = filteredParcels.length > 0 && filteredParcels.every(p => p.selected);
  const toggleSelectAll = () => setParcels(ps => ps.map(p => filteredParcels.find(f => f.id === p.id) ? { ...p, selected: !allSelected } : p));
  const toggleSelect = (id: string) => setParcels(ps => ps.map(p => p.id === id ? { ...p, selected: !p.selected } : p));
  const selectedParcels = parcels.filter(p => p.selected);

  const bulkApprove = () => {
    setParcels(ps => ps.map(p => p.selected && (p.status === 'pending' || p.status === 'verified') ? { ...p, status: 'approved', selected: false, reviewedBy: 'Admin: K. Freeman', reviewedDate: new Date().toISOString().slice(0, 10) } : { ...p, selected: false }));
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Header */}
        <div className="bg-slate-900 border-b border-slate-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link href="/parcel-delineation" className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm">
                <ArrowLeft size={16} />
                <span>Stage 2</span>
              </Link>
              <div className="w-px h-5 bg-slate-700" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-violet-600/30 border border-violet-500/40 rounded text-xs font-bold text-violet-300">S3</span>
                  <h1 className="text-xl font-bold text-white">Ownership Verification</h1>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">Verify parcel ownership against taxpayer registry · Flag disputes · Approve for Stage 4 valuation</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {forwardedCount > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-900/30 border border-emerald-700/40 rounded-lg text-xs text-emerald-300">
                  <CheckCircle size={12} />
                  {forwardedCount} forwarded to S4
                </div>
              )}
              <button
                onClick={() => setShowForwardModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-semibold transition-all"
              >
                <Send size={14} />
                Forward to S4
              </button>
            </div>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="px-6 py-4 grid grid-cols-7 gap-3">
          {[
            { label: 'Total Parcels', value: kpis.total, color: '#94a3b8', icon: Layers },
            { label: 'Pending Review', value: kpis.pending, color: '#94a3b8', icon: Clock },
            { label: 'Verified', value: kpis.verified, color: '#60a5fa', icon: Shield },
            { label: 'Disputed', value: kpis.disputed, color: '#f59e0b', icon: AlertTriangle },
            { label: 'Approved', value: kpis.approved, color: '#34d399', icon: CheckCircle },
            { label: 'Rejected', value: kpis.rejected, color: '#fb7185', icon: XCircle },
            { label: 'Registry Match Rate', value: `${kpis.matchRate}%`, color: '#a78bfa', icon: Users },
          ].map(({ label, value, color, icon: KpiIcon }) => (
            <div key={label} className="bg-slate-900 border border-slate-800 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <KpiIcon size={13} style={{ color }} />
                <span className="text-xs text-slate-500">{label}</span>
              </div>
              <p className="text-2xl font-bold" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-slate-800">
          <div className="flex gap-1">
            {([
              { id: 'queue', label: 'Verification Queue', icon: FileText },
              { id: 'registry', label: 'Taxpayer Registry', icon: Users },
              { id: 'analytics', label: 'Analytics', icon: BarChart2 },
            ] as { id: ActiveTab; label: string; icon: React.ElementType }[]).map(({ id, label, icon: TabIcon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                  activeTab === id
                    ? 'border-violet-500 text-violet-300'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <TabIcon size={14} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 py-5">

          {/* ── Queue Tab ── */}
          {activeTab === 'queue' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-48">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search parcel ID, owner, county…"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as VerificationStatus | 'all')}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="all">All Statuses</option>
                  {(Object.keys(STATUS_CONFIG) as VerificationStatus[]).map(s => (
                    <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                  ))}
                </select>
                <select
                  value={matchFilter}
                  onChange={e => setMatchFilter(e.target.value as typeof matchFilter)}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="all">All Match Types</option>
                  <option value="matched">Registry Match</option>
                  <option value="partial">Partial Match</option>
                  <option value="unmatched">No Match</option>
                </select>
                {selectedParcels.length > 0 && (
                  <button
                    onClick={bulkApprove}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-all"
                  >
                    <ThumbsUp size={13} />
                    Bulk Approve ({selectedParcels.length})
                  </button>
                )}
                <span className="text-xs text-slate-500 ml-auto">{filteredParcels.length} parcels</span>
              </div>

              {/* Table */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wide">
                      <th className="px-4 py-3 text-left w-10">
                        <button onClick={toggleSelectAll} className="text-slate-400 hover:text-white">
                          {allSelected ? <CheckSquare size={15} className="text-violet-400" /> : <Square size={15} />}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('parcelId')}>
                        <span className="flex items-center gap-1">Parcel ID <SortIcon field="parcelId" /></span>
                      </th>
                      <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('ownerName')}>
                        <span className="flex items-center gap-1">Owner <SortIcon field="ownerName" /></span>
                      </th>
                      <th className="px-4 py-3 text-left">County / Type</th>
                      <th className="px-4 py-3 text-left">Registry Match</th>
                      <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('registryConfidence')}>
                        <span className="flex items-center gap-1">Confidence <SortIcon field="registryConfidence" /></span>
                      </th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort('submittedDate')}>
                        <span className="flex items-center gap-1">Submitted <SortIcon field="submittedDate" /></span>
                      </th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredParcels.map((parcel, idx) => {
                      const statusCfg = STATUS_CONFIG[parcel.status];
                      const matchCfg = MATCH_CONFIG[parcel.registryMatch];
                      const StatusIcon = statusCfg.icon;
                      return (
                        <tr key={parcel.id} className={`border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors ${idx % 2 === 0 ? '' : 'bg-slate-900/40'}`}>
                          <td className="px-4 py-3">
                            <button onClick={() => toggleSelect(parcel.id)} className="text-slate-400 hover:text-white">
                              {parcel.selected ? <CheckSquare size={15} className="text-violet-400" /> : <Square size={15} />}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-slate-300">{parcel.parcelId}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-white font-medium">{parcel.ownerName}</p>
                              <p className="text-xs font-mono text-slate-500">{parcel.ownerTaxpayerId}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-slate-300">{parcel.county}</p>
                            <p className="text-xs text-slate-500">{parcel.propertyType}</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: matchCfg.color }} />
                              <span className="text-xs" style={{ color: matchCfg.color }}>{matchCfg.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${parcel.registryConfidence}%`,
                                    background: parcel.registryConfidence >= 80 ? '#34d399' : parcel.registryConfidence >= 50 ? '#f59e0b' : '#fb7185'
                                  }}
                                />
                              </div>
                              <span className="text-xs text-slate-400">{parcel.registryConfidence}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${statusCfg.bg}`} style={{ color: statusCfg.color }}>
                              <StatusIcon size={11} />
                              {statusCfg.label}
                            </span>
                            {parcel.disputeReason && (
                              <p className="text-xs text-amber-400 mt-0.5">{DISPUTE_LABELS[parcel.disputeReason]}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{parcel.submittedDate}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => setReviewParcel(parcel)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-violet-500 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition-all ml-auto"
                            >
                              <Eye size={12} />
                              Review
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredParcels.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-12 text-center text-slate-500 text-sm">
                          No parcels match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Registry Tab ── */}
          {activeTab === 'registry' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white">Taxpayer Registry Cross-Reference</h2>
                <span className="text-xs text-slate-500">{TAXPAYER_REGISTRY.length} registered taxpayers</span>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wide">
                      <th className="px-4 py-3 text-left">Taxpayer ID</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">National ID</th>
                      <th className="px-4 py-3 text-left">County</th>
                      <th className="px-4 py-3 text-left">Registered Parcels</th>
                      <th className="px-4 py-3 text-left">S3 Parcels</th>
                      <th className="px-4 py-3 text-left">Registry Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TAXPAYER_REGISTRY.map((entry, idx) => {
                      const linkedParcels = parcels.filter(p => p.ownerTaxpayerId === entry.taxpayerId);
                      return (
                        <tr key={entry.id} className={`border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors ${idx % 2 === 0 ? '' : 'bg-slate-900/40'}`}>
                          <td className="px-4 py-3 font-mono text-xs text-slate-300">{entry.taxpayerId}</td>
                          <td className="px-4 py-3 text-white font-medium">{entry.name}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-400">{entry.nationalId}</td>
                          <td className="px-4 py-3 text-slate-300">{entry.county}</td>
                          <td className="px-4 py-3 text-slate-300">{entry.registeredParcels}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {linkedParcels.length === 0 ? (
                                <span className="text-xs text-slate-600">—</span>
                              ) : linkedParcels.map(p => {
                                const cfg = STATUS_CONFIG[p.status];
                                return (
                                  <span key={p.id} className={`px-1.5 py-0.5 rounded text-xs font-mono ${cfg.bg}`} style={{ color: cfg.color }}>
                                    {p.parcelId.split('-').slice(-1)[0]}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${
                              entry.status === 'active' ? 'bg-emerald-900/40 text-emerald-300'
                              : entry.status === 'flagged'? 'bg-amber-900/40 text-amber-300' :'bg-slate-700/60 text-slate-400'
                            }`}>
                              {entry.status === 'active' ? <CheckCircle size={11} /> : entry.status === 'flagged' ? <AlertTriangle size={11} /> : <Clock size={11} />}
                              {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Analytics Tab ── */}
          {activeTab === 'analytics' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-5">
                {/* Status Breakdown */}
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4">Verification Status Breakdown</h3>
                  <div className="space-y-3">
                    {(Object.keys(STATUS_CONFIG) as VerificationStatus[]).map(s => {
                      const count = parcels.filter(p => p.status === s).length;
                      const pct = Math.round(count / parcels.length * 100);
                      const cfg = STATUS_CONFIG[s];
                      const SIcon = cfg.icon;
                      return (
                        <div key={s} className="flex items-center gap-3">
                          <SIcon size={13} style={{ color: cfg.color }} className="flex-shrink-0" />
                          <span className="text-xs text-slate-400 w-20">{cfg.label}</span>
                          <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: cfg.color }} />
                          </div>
                          <span className="text-xs font-semibold text-white w-6 text-right">{count}</span>
                          <span className="text-xs text-slate-500 w-8 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Registry Match Breakdown */}
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4">Registry Match Distribution</h3>
                  <div className="space-y-3">
                    {(['matched', 'partial', 'unmatched'] as const).map(m => {
                      const count = parcels.filter(p => p.registryMatch === m).length;
                      const pct = Math.round(count / parcels.length * 100);
                      const cfg = MATCH_CONFIG[m];
                      return (
                        <div key={m} className="flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
                          <span className="text-xs text-slate-400 w-24">{cfg.label}</span>
                          <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: cfg.color }} />
                          </div>
                          <span className="text-xs font-semibold text-white w-6 text-right">{count}</span>
                          <span className="text-xs text-slate-500 w-8 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-800">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">Overall Registry Match Rate</span>
                      <span className="text-lg font-bold text-emerald-400">{kpis.matchRate}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dispute Log */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4">Active Dispute Log</h3>
                {parcels.filter(p => p.status === 'disputed').length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">No active disputes.</p>
                ) : (
                  <div className="space-y-3">
                    {parcels.filter(p => p.status === 'disputed').map(p => (
                      <div key={p.id} className="flex items-start gap-3 bg-amber-900/10 border border-amber-800/30 rounded-lg p-3">
                        <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-mono text-xs text-slate-300">{p.parcelId}</span>
                            <span className="text-xs text-amber-300 font-medium">{p.disputeReason ? DISPUTE_LABELS[p.disputeReason] : 'Dispute'}</span>
                          </div>
                          <p className="text-xs text-slate-400">{p.ownerName} · {p.county}</p>
                          {p.disputeNote && <p className="text-xs text-amber-200/70 mt-1">{p.disputeNote}</p>}
                        </div>
                        <button
                          onClick={() => setReviewParcel(p)}
                          className="flex-shrink-0 px-2.5 py-1 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded text-xs transition-all"
                        >
                          Resolve
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* County Summary */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4">Verification by County</h3>
                <div className="grid grid-cols-3 gap-3">
                  {Array.from(new Set(parcels.map(p => p.county))).map(county => {
                    const cp = parcels.filter(p => p.county === county);
                    const approved = cp.filter(p => p.status === 'approved').length;
                    const disputed = cp.filter(p => p.status === 'disputed').length;
                    const pending = cp.filter(p => p.status === 'pending').length;
                    return (
                      <div key={county} className="bg-slate-800/60 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-2">
                          <MapPin size={12} className="text-slate-500" />
                          <span className="text-sm font-medium text-white">{county}</span>
                        </div>
                        <div className="flex gap-2 text-xs">
                          <span className="text-emerald-400">{approved} approved</span>
                          <span className="text-amber-400">{disputed} disputed</span>
                          <span className="text-slate-500">{pending} pending</span>
                        </div>
                        <div className="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden flex">
                          <div className="h-full bg-emerald-500" style={{ width: `${approved / cp.length * 100}%` }} />
                          <div className="h-full bg-amber-500" style={{ width: `${disputed / cp.length * 100}%` }} />
                          <div className="h-full bg-slate-600" style={{ width: `${pending / cp.length * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {reviewParcel && (
        <ReviewModal
          parcel={reviewParcel}
          onClose={() => setReviewParcel(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onFlag={handleFlag}
        />
      )}
      {showForwardModal && (
        <ForwardModal
          parcels={parcels}
          onClose={() => setShowForwardModal(false)}
          onConfirm={handleForwardConfirm}
        />
      )}
    </AppLayout>
  );
}
